import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import Axios from "axios";
import Swal from "sweetalert2";
import { updateData } from "../../utils/SamuiUtils";

export default function Topbar({ breadcrumbs, className = "", leftOffset = "var(--sidebar-width)" }) {
  const [authData, setAuthData] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changePasswordError, setChangePasswordError] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const res = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/authen`,
          {},
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              key: process.env.REACT_APP_ANALYTICS_KEY,
            },
          }
        );
        if (res.data.status === "OK") {
          setAuthData(res.data.decoded || {});
        } else {
          window.location.replace("/login");
        }
      } catch (err) {
        Swal.fire("หมดเวลา", "กรุณาเข้าสู่ระบบใหม่", "warning");
        window.location.replace("/login");
      }
    };

    fetchAuth();
  }, []);

  //  click ออกนอกปิด dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //  Logout
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "ออกจากระบบ?",
      text: "คุณแน่ใจหรือไม่ว่าต้องการออกจากระบบ",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ออกจากระบบ",
      cancelButtonText: "ยกเลิก",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");
        
        // เรียก logout endpoint เพื่อ revoke token
        if (token) {
          await Axios.post(
            `${process.env.REACT_APP_API_URL}/api/logout`,
            refreshToken ? { refreshToken } : {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
                key: process.env.REACT_APP_ANALYTICS_KEY,
              },
            }
          );
        }
      } catch (error) {
        console.error("Logout error:", error);
        // แม้จะเกิด error ก็ให้ logout ต่อไป
      } finally {
        // ลบข้อมูลจาก localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("expiresAt");
        localStorage.removeItem("emp_id");
        localStorage.removeItem("name");
        localStorage.removeItem("company");
        localStorage.removeItem("role");
        localStorage.removeItem("role_code");
        window.location.replace("/login");
      }
    }
  };

  // Revoke Token (สำหรับทดสอบ - revoke token โดยไม่ logout)
  const handleRevokeToken = async () => {
    const result = await Swal.fire({
      title: "Revoke Token?",
      text: "คุณต้องการ revoke token นี้หรือไม่? Token จะหมดอายุทันทีและ mobile app จะถูก redirect ไปหน้า login",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revoke Token",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#dc3545",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");
        
        if (!token) {
          Swal.fire("Error", "ไม่พบ token", "error");
          return;
        }

        const response = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/revoke-token`,
          refreshToken ? { refreshToken } : {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              key: process.env.REACT_APP_ANALYTICS_KEY,
            },
          }
        );

        if (response.data.status === "OK") {
          Swal.fire({
            title: "สำเร็จ",
            text: "Token ถูก revoke แล้ว! Mobile app จะถูก redirect ไปหน้า login เมื่อเรียก API ครั้งถัดไป",
            icon: "success",
            timer: 3000,
          });
        }
      } catch (error) {
        console.error("Revoke token error:", error);
        Swal.fire("Error", error.response?.data?.message || "เกิดข้อผิดพลาด", "error");
      }
    }
  };

  const handleOpenChangePassword = () => {
    setOpenMenu(false);
    setOldPassword("");
    setNewPassword("");
    setChangePasswordError("");
    setShowChangePasswordModal(true);
  };

  const handleCloseChangePassword = () => {
    if (isUpdatingPassword) return;
    setShowChangePasswordModal(false);
    setOldPassword("");
    setNewPassword("");
    setChangePasswordError("");
  };

  const handleChangePassword = async () => {
    if (!authData) {
      setChangePasswordError("ไม่สามารถตรวจสอบผู้ใช้งานได้");
      return;
    }

    const trimmedOldPassword = oldPassword.trim();
    const trimmedNewPassword = newPassword.trim();

    if (!trimmedOldPassword || !trimmedNewPassword) {
      setChangePasswordError("โปรดกรอกข้อมูลให้ครบถ้วน");
      return;
    }

    if (trimmedOldPassword === trimmedNewPassword) {
      setChangePasswordError("รหัสผ่านใหม่ต้องแตกต่างจากรหัสผ่านเดิม");
      return;
    }

    const currentPassword =
      authData?.Emp_PPass ?? authData?.emp_ppass ?? "";

    if (currentPassword && trimmedOldPassword !== currentPassword) {
      setChangePasswordError("รหัสผ่านเก่าไม่ถูกต้อง");
      return;
    }

    const employeeId = authData?.Emp_Id ?? authData?.emp_id;
    if (!employeeId) {
      setChangePasswordError("ไม่พบรหัสพนักงานสำหรับอัปเดตข้อมูล");
      return;
    }

    try {
      setIsUpdatingPassword(true);
      setChangePasswordError("");
      await updateData(
        "Tb_Set_Employee",
        { emp_ppass: trimmedNewPassword },
        { emp_id: employeeId }
      );

      Swal.fire("สำเร็จ", "เปลี่ยนรหัสผ่านเรียบร้อยแล้ว", "success");
      setAuthData((prev) =>
        prev
          ? {
            ...prev,
            Emp_PPass: trimmedNewPassword,
            emp_ppass: trimmedNewPassword,
          }
          : prev
      );
      setShowChangePasswordModal(false);
      setOldPassword("");
      setNewPassword("");
    } catch (error) {
      console.error("Error updating password:", error);
      setChangePasswordError("ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาลองใหม่");
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div
      className={`fixed top-0 left-0 right-0 h-14 bg-white border-b flex items-center justify-between pl-2 pr-6 shadow-sm z-[1200] ${className}`}
      style={{ left: leftOffset, right: 0 }}
    >
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-base font-medium leading-none text-gray-600">
        {breadcrumbs.map((b, i) => (
          <span key={i} className="flex items-center gap-2">
            {b.link ? (
              typeof b.link === "string" && b.link.startsWith("/") ? (
                <Link to={b.link} state={b.state} className="hover:underline">
                  {b.label}
                </Link>
              ) : (
                <a href={b.link} className="hover:underline">
                  {b.label}
                </a>
              )
            ) : (
              b.label
            )}
            {i < breadcrumbs.length - 1 && <span> &gt; </span>}
          </span>
        ))}
      </div>

      {/* User info + Avatar */}
      <div className="relative" ref={menuRef}>
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setOpenMenu((prev) => !prev)}
        >
          <div className="flex flex-col items-end">
            <span className="text-sm text-gray-700 font-medium">
              {authData?.Emp_Name || "User"}
            </span>
            <span className="text-xs text-gray-500">
              {authData?.PST_Name || ""} {authData?.Dept_Name || ""}
            </span>
          </div>
          <img
            src="/logo.png"
            alt="user avatar"
            className="w-8 h-8 rounded-full border hover:scale-110 transition"
          />
        </div>

        {/* Dropdown menu */}
        {openMenu && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border py-2 z-[100]">
            <button
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              onClick={handleOpenChangePassword}
            >
              เปลี่ยนรหัสผ่าน
            </button>
            <hr className="my-1" />
            <button
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-orange-600"
              onClick={handleRevokeToken}
            >
              <i className="fa fa-ban me-2"></i>
              Revoke Token (ทดสอบ)
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600"
              onClick={handleLogout}
            >
              ออกจากระบบ
            </button>
          </div>
        )}
      </div>
      {showChangePasswordModal &&
        createPortal(
          <>
            <div
              className="modal fade show"
              style={{ display: "block", position: "fixed", inset: 0, zIndex: 99999 }}
              tabIndex="-1"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                style={{ pointerEvents: "none", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <div className="modal-content bg-white" style={{ pointerEvents: "auto" }}>
                  <div className="modal-header">
                    <h3 className="modal-title">เปลี่ยนรหัสผ่าน</h3>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={handleCloseChangePassword}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {changePasswordError && (
                      <div className="alert alert-danger py-2 px-3" role="alert">
                        {changePasswordError}
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="form-label">รหัสผ่านเก่า</label>
                      <input
                        type="password"
                        className="form-control"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        disabled={isUpdatingPassword}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">รหัสผ่านใหม่</label>
                      <input
                        type="password"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isUpdatingPassword}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleCloseChangePassword}
                      disabled={isUpdatingPassword}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="button"
                      className="btn btn-success text-white"
                      onClick={handleChangePassword}
                      disabled={isUpdatingPassword}
                    >
                      {isUpdatingPassword ? "กำลังบันทึก..." : "บันทึก"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="modal-backdrop fade show"
              style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 99998 }}
            ></div>
          </>,
          document.body
        )}
    </div>
  );
}
